"use client";

import { c as _c } from "react/compiler-runtime";
import { Children, type FC, type ReactNode, useRef, useState } from "react";
import type { ComponentPropsWithoutChildren } from "@/lib/component-props";
import { cn } from "@/lib/utils";
import { scrollBelowStickyHeader } from "./scroll-below-sticky-header";
export interface PaginatedListProps extends ComponentPropsWithoutChildren<"div"> {
  children?: ReactNode;
  pageSize?: number;
}
export const PaginatedList: FC<PaginatedListProps> = t0 => {
  const $ = _c(43);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      pageSize: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const pageSize = t1 === undefined ? 3 : t1;
  const listRef = useRef(null);
  const items = Children.toArray(children);
  const totalPages = Math.ceil(items.length / pageSize);
  const [currentPage, setCurrentPage] = useState(0);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = pageIndex => {
      setCurrentPage(pageIndex);
      const list = listRef.current;
      if (list) {
        scrollBelowStickyHeader(list);
      }
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const goToPage = t2;
  if (items.length === 0) {
    return null;
  }
  if (totalPages <= 1) {
    let t3;
    if ($[6] !== children || $[7] !== className || $[8] !== props) {
      t3 = <div className={className} {...props}>{children}</div>;
      $[6] = children;
      $[7] = className;
      $[8] = props;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  const canGoPrev = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;
  const t3 = Array.from({
    length: totalPages
  }, _temp).map(pageIndex_0 => <div key={`page-group-${pageIndex_0}`} className={pageIndex_0 === currentPage ? "contents" : "hidden"}>{items.slice(pageIndex_0 * pageSize, (pageIndex_0 + 1) * pageSize)}</div>);
  const t4 = !canGoPrev;
  let t5;
  if ($[10] !== currentPage || $[11] !== goToPage) {
    t5 = () => goToPage(currentPage - 1);
    $[10] = currentPage;
    $[11] = goToPage;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = canGoPrev ? "cursor-pointer bg-[var(--bg-primary)] hover:bg-[var(--bg-secondary)] hover:shadow-[2px_2px_0_0_var(--fg-primary)]" : "cursor-not-allowed opacity-40";
  let t7;
  if ($[13] !== t6) {
    t7 = cn("inline-flex size-11 items-center justify-center rounded-lg border-[3px] border-[var(--fg-primary)] font-bold shadow-[4px_4px_0_0_var(--fg-primary)] transition-[background-color,box-shadow] focus-visible:outline-2 focus-visible:outline-transparent focus-visible:outline-offset-2 focus-visible:focus-ring-sm", t6);
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t7) {
    t8 = <button type="button" disabled={t4} onClick={t5} aria-label="Previous page" className={t7}>←</button>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== totalPages) {
    t9 = Array.from({
      length: totalPages
    }, _temp2);
    $[19] = totalPages;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== currentPage || $[22] !== goToPage || $[23] !== t9) {
    t10 = t9.map(pageIndex_1 => <button key={`page-${pageIndex_1}`} type="button" onClick={() => goToPage(pageIndex_1)} aria-label={`Page ${pageIndex_1 + 1}`} aria-current={pageIndex_1 === currentPage ? "page" : undefined} className={cn("inline-flex size-11 items-center justify-center rounded-lg border-[3px] border-[var(--fg-primary)] font-[family-name:var(--font-geist-mono)] text-sm font-bold transition-[background-color,box-shadow] focus-visible:outline-2 focus-visible:outline-transparent focus-visible:outline-offset-2 focus-visible:focus-ring-sm", pageIndex_1 === currentPage ? "bg-[var(--fg-primary)] text-[var(--fg-inverse)] shadow-[4px_4px_0_0_var(--fg-primary)]" : "cursor-pointer bg-[var(--bg-primary)] shadow-[4px_4px_0_0_var(--fg-primary)] hover:bg-[var(--bg-secondary)] hover:shadow-[2px_2px_0_0_var(--fg-primary)]")}>{pageIndex_1 + 1}</button>);
    $[21] = currentPage;
    $[22] = goToPage;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = !canGoNext;
  let t12;
  if ($[25] !== currentPage || $[26] !== goToPage) {
    t12 = () => goToPage(currentPage + 1);
    $[25] = currentPage;
    $[26] = goToPage;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = canGoNext ? "cursor-pointer bg-[var(--bg-primary)] hover:bg-[var(--bg-secondary)] hover:shadow-[2px_2px_0_0_var(--fg-primary)]" : "cursor-not-allowed opacity-40";
  let t14;
  if ($[28] !== t13) {
    t14 = cn("inline-flex size-11 items-center justify-center rounded-lg border-[3px] border-[var(--fg-primary)] font-bold shadow-[4px_4px_0_0_var(--fg-primary)] transition-[background-color,box-shadow] focus-visible:outline-2 focus-visible:outline-transparent focus-visible:outline-offset-2 focus-visible:focus-ring-sm", t13);
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== t14) {
    t15 = <button type="button" disabled={t11} onClick={t12} aria-label="Next page" className={t14}>→</button>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t10 || $[35] !== t15 || $[36] !== t8) {
    t16 = <nav aria-label="Pagination" className="flex items-center justify-center gap-3 pt-10">{t8}{t10}{t15}</nav>;
    $[34] = t10;
    $[35] = t15;
    $[36] = t8;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== className || $[39] !== props || $[40] !== t16 || $[41] !== t3) {
    t17 = <div className={className} {...props} ref={listRef}>{t3}{t16}</div>;
    $[38] = className;
    $[39] = props;
    $[40] = t16;
    $[41] = t3;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
};
function _temp(_, i) {
  return i;
}
function _temp2(__0, i_0) {
  return i_0;
}